import '../styles/presence.css';
import '../styles/trackList.css';
import './MusicBar.css';
import html from './MusicBar.html';
import { partyStreamUrl } from '../api/embyApi';
import { connectParty, issueCastToken, leaveParty, revokeCastToken } from '../api/party';
import type { PartyConnection } from '../api/party';
import { colorForName, escapeHtml, formatDuration, initialsFor } from '../tiles';
import { showToast } from '../toast';
import type { MusicCurrentPublic, MusicPublicState, PartyPublicState, SocketMember } from '../types';
import { initCast, localDriver, onCastAvailabilityChanged, onCastSessionEnded, startCast, stopCast } from '../cast';
import type { PlaybackDriver } from '../cast';
import { clearMediaSession, setMediaSessionActionHandlers, setMediaSessionIdle, setMediaSessionMetadata, setMediaSessionNextAction, setMediaSessionPlaybackState, setMediaSessionPositionState } from '../mediaSession';

/**
 * Persistent, app-lifetime "listen party" bar - mounted ONCE by Shell.ts
 * (see renderShell), living outside the router's #mainContent so it
 * survives every navigation instead of being torn down like a normal
 * route (contrast with the old, deleted views/musicPlayer/, which WAS a
 * route - see docs/architecture.md). This is what actually lets someone
 * browse the library/an artist/an album and add tracks to the shared
 * queue while still listening, which a full-page route can't do without
 * losing player state on every click away from it.
 *
 * Because this module is a singleton rather than something re-rendered
 * per view, its lifecycle is three plain functions instead of the
 * render()-returns-a-CleanupFn pattern the rest of the client uses:
 * initMusicBar() wires the static DOM once at boot, joinMusicParty()/
 * leaveMusicParty() attach/detach a live party on top of it. There is no
 * per-visit `root.innerHTML = html` here - the DOM (and its event
 * listeners) are set up exactly once and just get shown/hidden and
 * re-populated in place, which is also why this doesn't need router.ts's
 * cleanup-function contract at all: it's never registered as a route
 * handler in the first place - see views/player/Player.ts's dispatcher,
 * which calls joinMusicParty/leaveMusicParty instead of rendering a route
 * for music parties.
 */

// #playPauseBtn/#miniPlayPauseBtn's two states - real SVGs (not the old
// '▶'/'⏸' text glyphs, which never matched Player.ts's own icon-button
// convention used everywhere else this app shows a play/pause control).
const PLAY_ICON_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4.5v15l14-7.5Z" fill="currentColor"></path></svg>';
const PAUSE_ICON_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="4" width="5" height="16" rx="1" fill="currentColor"></rect><rect x="14" y="4" width="5" height="16" rx="1" fill="currentColor"></rect></svg>';

// See views/player/Player.ts's own HLS_CONFIG for why these are raised
// well above hls.js's defaults (10s manifest/level/frag load timeouts) -
// every track here also goes through CatalogService.proxyStream into a
// fresh Emby transcode, same latency risk as the video player. Duplicated
// rather than shared - the two players' hls.js setup is already
// independently duplicated for the same reason (see loadTrack below).
const HLS_CONFIG: HlsConfig = {
  manifestLoadingTimeOut: 30000,
  manifestLoadingMaxRetry: 2,
  levelLoadingTimeOut: 30000,
  fragLoadingTimeOut: 60000
};

let barEl: HTMLElement | null = null;
let activePartyId: string | null = null;
let conn: PartyConnection | null = null;
let hls: HlsInstance | null = null;
let currentItemId: string | null = null;
let currentDurationSeconds = 0;
let suppressLocalEvents = false; // avoid feeding our own correction back as a new event - same pattern as Player.ts/MusicPlayer.ts
let positionTicker: number | null = null;
// "The player" every read/write below actually targets - the local
// <audio> by default, swapped for a Chromecast's RemotePlayer while
// casting (see startCasting/cast.ts). Set for real once initMusicBar has
// an <audio> element to wrap; null only before that first call. Unlike
// Player.ts, there's no host gate on any of this - a music party has no
// host at all (see the queueList comment in MusicBar.html), so casting
// here is exactly as symmetric as play/pause already is: whoever casts
// is just routing their own copy of the shared playback through a TV/
// speaker instead of this tab, same as plugging in headphones would.
let activeDriver: PlaybackDriver | null = null;
let isCasting = false;
// Held between issueCastToken (startCasting) and revokeCastToken
// (stopCasting) so a track change mid-cast (see loadTrack) can reload
// the Chromecast's media without minting a fresh token for every track.
let castToken: string | null = null;
let latestMembers: SocketMember[] = [];
// Cached purely so renderMembers (a member joining/leaving) can recompute
// the vote-skip label on its own - "votes needed" depends on the
// connected-member count, which changes independently of any actual
// vote/track-change event (the only two things that used to refresh it).
let lastMusicState: MusicPublicState | null = null;

/** Whatever party is currently attached to the bar, if any - used by
 *  views/album to decide whether a track's button should start a brand
 *  new party or add to this one's queue instead (see Album.ts). */
export function getActiveMusicPartyId(): string | null {
  return activePartyId;
}

/** Wires the bar's static DOM once - called by Shell.ts right after it
 *  renders the shell chrome. `root` stays in the DOM (just hidden via
 *  .hide) for the rest of the session, sibling to #mainContent. */
export function initMusicBar(root: HTMLElement): void {
  barEl = root;
  root.innerHTML = html;
  root.classList.add('hide');

  const audio = barEl.querySelector('#barAudio') as HTMLAudioElement;
  const playPauseBtn = barEl.querySelector('#playPauseBtn') as HTMLButtonElement;
  const miniPlayPauseBtn = barEl.querySelector('#miniPlayPauseBtn') as HTMLButtonElement;
  const castBtn = barEl.querySelector('#castBtn') as HTMLButtonElement;
  const voteSkipBtn = barEl.querySelector('#voteSkipBtn') as HTMLButtonElement;
  const toggleQueueBtn = barEl.querySelector('#toggleQueueBtn')!;
  const queuePanel = barEl.querySelector('#musicBarQueue')!;
  const queueCloseBtn = barEl.querySelector('#queueCloseBtn')!;
  const leaveBtn = barEl.querySelector('#leaveBtn')!;
  const trackProgress = barEl.querySelector('#trackProgress') as HTMLElement;
  const mobileMiniBar = barEl.querySelector('#mobileMiniBar')!;
  const musicBarPanel = barEl.querySelector('#musicBarPanel')!;
  const panelCloseBtn = barEl.querySelector('#panelCloseBtn')!;

  activeDriver = localDriver(audio);

  audio.addEventListener('play', () => { if (!suppressLocalEvents && conn) conn.setMusicPaused(false); });
  audio.addEventListener('pause', () => { if (!suppressLocalEvents && conn) conn.setMusicPaused(true); });
  playPauseBtn.addEventListener('click', () => conn?.setMusicPaused(!activeDriver!.paused));
  miniPlayPauseBtn.addEventListener('click', () => conn?.setMusicPaused(!activeDriver!.paused));
  voteSkipBtn.addEventListener('click', () => conn?.voteSkip());
  toggleQueueBtn.addEventListener('click', () => queuePanel.classList.toggle('hide'));
  queueCloseBtn.addEventListener('click', () => queuePanel.classList.add('hide'));
  // Desktop-popover convention (same as views/player/Player.ts's own
  // settingsPanel) - closes on any click that lands neither inside the
  // panel nor on the button that opens it. Harmless on mobile's
  // full-screen layout too (there's no "outside" to click there, but
  // #queueCloseBtn above is the real fix for that case - see
  // MusicBar.css's own comment on why the old position: relative left
  // that button unreachable once the queue was open).
  document.addEventListener('click', (e) => {
    if (queuePanel.classList.contains('hide')) return;
    const target = e.target as Node;
    if (!queuePanel.contains(target) && !toggleQueueBtn.contains(target)) {
      queuePanel.classList.add('hide');
    }
  });
  leaveBtn.addEventListener('click', () => leaveMusicParty());

  // Mobile only (see MusicBar.css's 720px breakpoint - .musicBarPanel is
  // already always visible on desktop, so these two are simply never
  // clicked/reachable there). The mini bar itself is the tap target
  // (not just an icon on it) so there's a generous hit area on a touch
  // screen - #miniPlayPauseBtn's own click above stops that one
  // specific tap from also opening the panel via this listener.
  mobileMiniBar.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('#miniPlayPauseBtn')) return;
    musicBarPanel.classList.add('open');
  });
  panelCloseBtn.addEventListener('click', () => musicBarPanel.classList.remove('open'));

  // Click/drag to seek - no host check, unlike the video player's own
  // seek bar, since a listen party has no host at all (see activeDriver's
  // own comment) - anyone dragging this updates their own driver
  // immediately (for instant visual feedback, same as the video player's
  // own seekFromPointerEvent) and broadcasts it, same "anyone, straight
  // away" treatment play/pause already has (see MusicQueueService.seek).
  // Pointer events (not click/drag) so mouse and touch share one
  // implementation, and setPointerCapture keeps the drag tracking even if
  // the pointer leaves the track's own bounds mid-gesture - same pattern
  // as Player.ts's progressBar.
  let isDraggingProgress = false;

  function seekTo(seconds: number): void {
    if (currentDurationSeconds <= 0) return;
    activeDriver!.currentTime = Math.min(Math.max(0, seconds), currentDurationSeconds);
    updatePositionDisplay();
    conn?.seekMusic(activeDriver!.currentTime);
  }

  function seekFromPointerEvent(e: PointerEvent): void {
    if (currentDurationSeconds <= 0) return;
    const rect = trackProgress.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    seekTo(fraction * currentDurationSeconds);
  }

  trackProgress.addEventListener('pointerdown', (e) => {
    isDraggingProgress = true;
    trackProgress.setPointerCapture(e.pointerId);
    seekFromPointerEvent(e);
  });
  trackProgress.addEventListener('pointermove', (e) => {
    if (!isDraggingProgress) return;
    seekFromPointerEvent(e);
  });
  trackProgress.addEventListener('pointerup', () => { isDraggingProgress = false; });
  trackProgress.addEventListener('pointercancel', () => { isDraggingProgress = false; });

  // Wired once, like Player.ts's own equivalent - no host concept here
  // (see activeDriver's own comment) so, unlike there, play/pause never
  // need to branch into a request-instead-of-act path.
  setMediaSessionActionHandlers({
    onPlay: () => conn?.setMusicPaused(false),
    onPause: () => conn?.setMusicPaused(true),
    onSeek: seekTo
  });

  // Visible to any member the moment a Chromecast is on the network - not
  // gated on anything party-related, same as Player.ts's own castBtn
  // (see its comment: casting is available to any member, and this bar
  // is only in the DOM/visible at all once a party is actually joined,
  // so there's nothing extra to gate here). Both subscriptions are
  // page-lifetime, never unsubscribed - see initMusicBar's own header
  // comment: this whole module is wired once at boot and never torn
  // down, unlike a router-driven view.
  initCast();
  onCastAvailabilityChanged((available) => castBtn.classList.toggle('hide', !available));
  onCastSessionEnded(endCastAndResync);
  castBtn.addEventListener('click', () => { if (isCasting) endCastAndResync(); else startCasting(); });

  // Ticks the visible position label AND the progress bar's fill between
  // server updates, same "derive it client-side from a snapshot" approach
  // as Home's party-elapsed labels and the old MusicPlayer.ts - runs
  // continuously rather than only while a party is active (cheap: a
  // no-op while !currentItemId). Also called directly right after a local
  // seek (see seekFromPointerEvent above), for instant feedback instead
  // of waiting up to 500ms for this same tick to catch up.
  positionTicker = window.setInterval(updatePositionDisplay, 500);
}

function updatePositionDisplay(): void {
  if (!currentItemId) return;
  const currentTime = activeDriver!.currentTime;
  const position = barEl!.querySelector('#trackPosition')!;
  position.textContent = `${formatDuration(currentTime)} / ${formatDuration(currentDurationSeconds)}`;
  const percent = currentDurationSeconds > 0 ? `${Math.min(100, (currentTime / currentDurationSeconds) * 100)}%` : '0%';
  (barEl!.querySelector('#trackProgressFill') as HTMLElement).style.width = percent;
  // Same passive indicator on the collapsed mobile preview - see
  // MusicBar.css's .mobileMiniProgress. Kept a plain width sync rather
  // than its own pointer-seek wiring: dragging is one tap (expand the
  // panel) away, see #mobileMiniBar's own click handler.
  (barEl!.querySelector('#miniProgressFill') as HTMLElement).style.width = percent;
  setMediaSessionPositionState(currentDurationSeconds, currentTime);
}

/**
 * Attaches the bar to a music party - called from Player.ts's dispatcher
 * (both for a freshly created party and for joining one already live) and
 * directly from Album.ts when starting a new party without leaving the
 * album page. Safe to call again for the SAME party (no-op) or for a
 * DIFFERENT one (leaves the old one first, exactly like clicking "Quitter"
 * would - a listener can only meaningfully be in one listen party at once).
 */
export function joinMusicParty(partyId: string, party: PartyPublicState): void {
  if (activePartyId === partyId) return;
  if (activePartyId) leaveMusicParty();

  activePartyId = partyId;
  barEl!.classList.remove('hide');
  if (party.music) applyMusicState(party.music);

  conn = connectParty(partyId, {
    onMembers: renderMembers,
    onQueueUpdated: applyMusicState,
    onDeviceConflict: () => {
      // Same take-over prompt as views/player/Player.ts - see its own
      // comment. Declining here means leaving the bar entirely (there's no
      // "page" to just navigate away from, unlike the video player).
      const takeOver = confirm(
        "Tu es déjà connecté à cette party depuis un autre appareil.\n\nContinuer ici va déconnecter l'autre appareil."
      );
      if (takeOver) conn?.confirmDeviceTakeover();
      else leaveMusicParty();
    },
    onError: (message) => showToast(message, { variant: 'error' }),
    // Fires for every disconnect, intentional or not - leaveMusicParty
    // itself already clears activePartyId synchronously, well before its
    // own conn.leave() actually closes the socket, so seeing it still set
    // here means this device's connection died some other way (most
    // often: replaced by a takeover on another device - see
    // SocketGateway's "join" handler). Without this, the bar just sat
    // there looking/sounding fully live forever on the old device, on a
    // conn that was actually already dead underneath it - reported
    // directly as "both devices end up on the session, controls never
    // transfer." leaveMusicParty() is safe to call here even though the
    // party already moved on: leaveParty's own deviceId check (see
    // api/party.ts) makes its REST call a no-op for a stale device
    // instead of ending the CURRENT device's membership by mistake.
    onClose: () => {
      if (!activePartyId) return;
      showToast('Connexion à la party interrompue.', { variant: 'error' });
      leaveMusicParty();
    }
  });
}

/**
 * Detaches the bar from whatever party it's currently in - the "Quitter"
 * button's handler, and also called by Player.ts's dispatcher before
 * entering ANY video party (see its own comment: you can't stay in a
 * shared audio queue while also joining a video watch party, so starting
 * one always exits the other first). A harmless no-op when nothing is
 * active, so every call site can call it unconditionally rather than
 * checking getActiveMusicPartyId() itself first.
 */
export function leaveMusicParty(): void {
  if (!activePartyId) return;
  const partyId = activePartyId;
  activePartyId = null;

  // Actually ends the Chromecast session (not just a local driver swap) -
  // leaving the party means there's no more shared state left to cast at
  // all, unlike just navigating between browsing pages while it plays on
  // (see Player.ts's own cleanup for why THAT case deliberately leaves an
  // active cast running).
  if (isCasting) stopCasting();

  if (hls) { hls.destroy(); hls = null; }
  const audio = barEl!.querySelector('#barAudio') as HTMLAudioElement;
  audio.pause();
  audio.removeAttribute('src');
  currentItemId = null;
  lastMusicState = null;
  latestMembers = [];
  clearMediaSession();

  conn?.leave();
  conn = null;

  barEl!.classList.add('hide');
  barEl!.querySelector('#musicBarQueue')!.classList.add('hide');
  barEl!.querySelector('#musicBarPanel')!.classList.remove('open');

  leaveParty(partyId).catch(() => {});
}

/**
 * Casts (or re-casts) `activePartyId`'s current shared stream - the same
 * partyStreamUrl every listener's own <audio> already points at (see
 * loadTrack), so this never opens a second Emby stream, same guarantee
 * as Player.ts's video casting. Called once by startCasting to begin a
 * session, and again by loadTrack for every later track change: CAF
 * sessions support loading new media into an already-connected session
 * without a fresh device picker (that's what requestSession() resolving
 * immediately for an existing session is for - see cast.ts), which is
 * exactly what a listen party needs and the video player never did
 * (there, any item change just drops casting entirely and starts over
 * locally - see Player.ts's loadVideo - judged not worth mirroring here
 * given how often a queue actually advances, far more often than a video
 * party changes items). `_` cache-busts the request: the URL itself never
 * encodes which track is "current" (the server decides that - see
 * partyStreamUrl's own comment), so without this a Chromecast receiver
 * that happened to cache the previous track's manifest would have no
 * reason to ever re-fetch it.
 */
async function castTrack(title: string): Promise<void> {
  const contentUrl = `${location.origin}${partyStreamUrl(activePartyId!)}?castToken=${encodeURIComponent(castToken!)}&_=${Date.now()}`;
  activeDriver = await startCast(contentUrl, title, onCastStateChanged);
}

/** The Chromecast's own remote/on-screen controls changed play/pause or
 *  seeked - broadcast it exactly like the local <audio>'s native play/
 *  pause listeners already do (see initMusicBar), since casting here has
 *  no host gate to check first (see activeDriver's own comment). */
function onCastStateChanged(): void {
  setPlayIcon(!activeDriver!.paused);
  if (!suppressLocalEvents) conn?.setMusicPaused(activeDriver!.paused);
}

/** Both play/pause buttons - the main one inside .musicBarPanel and the
 *  collapsed mobile preview's own - always show the same state, so
 *  every call site that used to touch just #playPauseBtn's textContent
 *  updates both here instead of duplicating the innerHTML swap at each
 *  one. */
function setPlayIcon(isPlaying: boolean): void {
  const icon = isPlaying ? PAUSE_ICON_SVG : PLAY_ICON_SVG;
  (barEl!.querySelector('#playPauseBtn') as HTMLButtonElement).innerHTML = icon;
  (barEl!.querySelector('#miniPlayPauseBtn') as HTMLButtonElement).innerHTML = icon;
  setMediaSessionPlaybackState(isPlaying);
}

/** Same pairing as setPlayIcon above, for the track metadata shown in
 *  both .musicBarPanel and the collapsed mobile preview. */
function setTrackMeta(imageUrl: string, name: string, artist: string): void {
  const trackImage = barEl!.querySelector('#trackImage') as HTMLImageElement;
  const miniTrackImage = barEl!.querySelector('#miniTrackImage') as HTMLImageElement;
  trackImage.src = imageUrl;
  trackImage.classList.remove('noImage');
  miniTrackImage.src = imageUrl;
  miniTrackImage.classList.remove('noImage');
  barEl!.querySelector('#trackName')!.textContent = name;
  barEl!.querySelector('#miniTrackName')!.textContent = name;
  barEl!.querySelector('#trackArtist')!.textContent = artist;
  barEl!.querySelector('#miniTrackArtist')!.textContent = artist;
}

/**
 * Starts casting - available to any listener, not just... there is no
 * "just", a listen party has no host (see activeDriver's own comment).
 * Mints a short-lived castToken the same way Player.ts does (
 * CastTokenService, scoped to this party+member) and silences the local
 * <audio> (nothing left for this device to usefully play while the TV
 * does), then hands off to castTrack for the actual load.
 */
async function startCasting(): Promise<void> {
  const partyId = activePartyId;
  if (!partyId || isCasting || !lastMusicState?.current) return;
  const track = lastMusicState.current;
  try {
    const { token } = await issueCastToken(partyId);
    castToken = token;
    await castTrack([track.itemName, track.artistName].filter(Boolean).join(' - '));

    if (hls) { hls.destroy(); hls = null; }
    const audio = barEl!.querySelector('#barAudio') as HTMLAudioElement;
    suppressLocalEvents = true;
    audio.pause();
    setTimeout(() => { suppressLocalEvents = false; }, 250);

    isCasting = true;
    (barEl!.querySelector('#castBtn') as HTMLButtonElement).setAttribute('aria-pressed', 'true');
  } catch (err) {
    // The user cancelling the device picker rejects the same way a real
    // failure does - neither is worth an alert(), just keep listening
    // locally, same as any other "the thing the user tried didn't work"
    // path in this app (see loadTrack's own catch for the same instinct).
    console.error('[watchparty musicBar] cast failed', err);
    castToken = null;
    revokeCastToken(partyId).catch(() => {});
  }
}

/** Pure teardown - drops back to the local driver and ends the actual
 *  Chromecast session/token, but does NOT resume local playback itself
 *  (see endCastAndResync, the only other caller that needs that, and
 *  leaveMusicParty, which doesn't - it's about to tear the local <audio>
 *  down too). */
function stopCasting(): void {
  if (!isCasting) return;
  isCasting = false;
  const audio = barEl!.querySelector('#barAudio') as HTMLAudioElement;
  activeDriver = localDriver(audio);
  (barEl!.querySelector('#castBtn') as HTMLButtonElement).setAttribute('aria-pressed', 'false');
  stopCast();
  if (activePartyId) revokeCastToken(activePartyId).catch(() => {});
  castToken = null;
}

/** Used by both the cast button's own "stop casting" click and a session
 *  ending on its own (the receiver's remote, the TV losing power) -
 *  captures wherever the TV left off before tearing the cast driver
 *  down, then resumes local playback from there via the same loadTrack
 *  path a genuine track change already uses. */
function endCastAndResync(): void {
  if (!isCasting) return;
  const resumeSeconds = activeDriver!.currentTime;
  const wasPlaying = !activeDriver!.paused;
  stopCasting();
  if (lastMusicState?.current) loadTrack(lastMusicState.current, resumeSeconds, !wasPlaying);
}

/**
 * A new track became current - called from applyMusicState, whether
 * that's a genuine queue advance or this client only just joined. Always
 * updates the displayed metadata; which of the two very differently-
 * shaped things actually produces sound is a separate decision
 * (isCasting) delegated to castTrack/loadTrackLocal below.
 */
function loadTrack(track: MusicCurrentPublic, elapsedSeconds: number, isPaused: boolean): void {
  currentItemId = track.itemId;
  currentDurationSeconds = track.durationSeconds || 0;
  setTrackMeta(track.itemImageUrl || '', track.itemName || '', [track.artistName, track.albumName].filter(Boolean).join(' - '));
  // Artist/album kept separate here, unlike setTrackMeta's own combined
  // display line above - the OS's own now-playing UI has real fields for
  // both, no need to pre-join them into one string for it.
  setMediaSessionMetadata(track.itemName || '', track.artistName || '', track.albumName || '', track.itemImageUrl || '');

  if (isCasting) {
    castTrack([track.itemName, track.artistName].filter(Boolean).join(' - ')).catch((err) => {
      // The Chromecast dropping mid-party (network hiccup, the TV losing
      // power without CAF noticing yet) must not strand the party unable
      // to advance - fall back to local playback, same recovery instinct
      // as the try/catch around hls.js below.
      console.error('[watchparty musicBar] failed to (re)cast the new track, falling back to local playback', err);
      stopCasting();
      loadTrackLocal(track, elapsedSeconds, isPaused);
    });
    return;
  }
  loadTrackLocal(track, elapsedSeconds, isPaused);
}

// Same HLS pipeline as the video player and the old MusicPlayer.ts -
// Emby's PlaybackInfo negotiation isn't video-specific, so a music
// track's TranscodingUrl just as plausibly comes back HLS-shaped, and
// most browsers besides Safari have no native .m3u8 support at all.
// Party-shared now too (PartyStreamService), same as the video player -
// every listener points at the same partyStreamUrl(activePartyId), one
// negotiated Emby session for the whole listen party instead of each
// listener's own independent one. There's no audio-track/quality picker
// here to worry about (never existed for music), so unlike the video
// player this needed no new UI at all.
function loadTrackLocal(track: MusicCurrentPublic, elapsedSeconds: number, isPaused: boolean): void {
  const audio = barEl!.querySelector('#barAudio') as HTMLAudioElement;

  if (hls) { hls.destroy(); hls = null; }

  const src = partyStreamUrl(activePartyId!); // loadTrack is only ever called while a party is active - see applyMusicState/joinMusicParty
  const resume = () => {
    suppressLocalEvents = true;
    audio.currentTime = elapsedSeconds || 0;
    if (!isPaused) audio.play().catch(() => {});
    setTimeout(() => { suppressLocalEvents = false; }, 250);
  };

  // Caught, not left to propagate - see MusicPlayer.ts's original comment
  // on this same try/catch: a bad manifest/network hiccup for THIS track
  // must not take the queue list and vote button down with it, since a
  // skip vote is the actual recovery path, not a crashed bar.
  try {
    if (window.Hls && window.Hls.isSupported()) {
      hls = new window.Hls(HLS_CONFIG);
      hls.on(window.Hls.Events.MANIFEST_PARSED, resume);
      hls.on(window.Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) console.error('[watchparty musicBar] HLS error', data);
      });
      hls.loadSource(src);
      hls.attachMedia(audio);
    } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      audio.src = src;
      audio.addEventListener('loadedmetadata', resume, { once: true });
    } else {
      console.error('[watchparty musicBar] no HLS support and hls.js unavailable');
    }
  } catch (err) {
    console.error('[watchparty musicBar] failed to load track audio', err);
  }
}

/**
 * "Votes needed" is a strict majority of currently CONNECTED members
 * (mirrors MusicQueueService's own math server-side) - it depends on
 * *two* independent things, the vote tally (music.skipVotes, only known
 * on a "queueUpdated" push) and the connected-member count (only known
 * on a "members" push). Recomputing it from whichever one last changed
 * - not just whenever a vote itself happens - is the whole fix for a
 * member joining/leaving not updating the "X/Y" shown: previously this
 * was inlined into applyMusicState only, so renderMembers changing the
 * member count had nothing to make the button's text catch up until the
 * next unrelated queueUpdated (a vote, a track change) happened to fire.
 */
function updateVoteSkipLabel(): void {
  const voteSkipBtn = barEl!.querySelector('#voteSkipBtn') as HTMLButtonElement;
  const voteBadge = barEl!.querySelector('#voteBadge') as HTMLElement;
  const current = lastMusicState?.current;
  voteSkipBtn.disabled = !current;
  if (!current) {
    voteBadge.classList.add('hide');
    return;
  }
  const connectedCount = latestMembers.filter((m) => m.connected).length || 1;
  const needed = Math.floor(connectedCount / 2) + 1;
  voteBadge.textContent = `${lastMusicState!.skipVotes}/${needed}`;
  voteBadge.classList.remove('hide');
}

function applyMusicState(music: MusicPublicState): void {
  lastMusicState = music;

  const playPauseBtn = barEl!.querySelector('#playPauseBtn') as HTMLButtonElement;
  const miniPlayPauseBtn = barEl!.querySelector('#miniPlayPauseBtn') as HTMLButtonElement;
  const castBtn = barEl!.querySelector('#castBtn') as HTMLButtonElement;
  const queueList = barEl!.querySelector('#queueList')!;
  const audio = barEl!.querySelector('#barAudio') as HTMLAudioElement;

  // Queue/vote-button/play-icon updates happen first, unconditionally,
  // deliberately BEFORE the track-loading branch below (see loadTrack's
  // own comment on why - same ordering as Player.ts/the old MusicPlayer.ts).
  setPlayIcon(Boolean(music.current && !music.current.isPaused));
  playPauseBtn.disabled = !music.current;
  miniPlayPauseBtn.disabled = !music.current;
  // Nothing for startCasting to actually cast with an empty queue - same
  // "disable, don't just silently no-op" treatment as playPauseBtn above.
  castBtn.disabled = !music.current && !isCasting;
  updateVoteSkipLabel();
  // A *registered* nexttrack handler is what makes the OS show the
  // control at all (see setMediaSessionNextAction's own comment) - same
  // "disable, don't just silently no-op" case as playPauseBtn/castBtn
  // above, just with no disabled state of its own to set instead.
  setMediaSessionNextAction(music.current ? () => conn?.voteSkip() : null);

  // Its own .queueRow, NOT views/album's shared .trackRow (see
  // styles/trackList.css) - that one is a CSS grid whose 4 columns
  // (28px index / 1fr name / auto duration / auto button) assume
  // Album.ts's own row shape. Reusing it here used to crush the track
  // NAME into the 28px column meant for a track number, since this
  // queue's rows never had an index at all - a real, visibly broken
  // layout bug, not just a style mismatch. See docs/architecture.md.
  queueList.innerHTML = music.queue.length
    ? music.queue.map((t) => `
      <div class="queueRow">
        <img class="queueRowArt" src="${escapeHtml(t.itemImageUrl || '')}" alt="" onerror="this.classList.add('noImage')" />
        <div class="queueRowInfo">
          <span class="queueRowName">${escapeHtml(t.itemName)}</span>
          <span class="hint queueRowMeta">${escapeHtml([t.artistName, t.albumName, `Ajouté par ${t.addedByUserName}`].filter(Boolean).join(' · '))}</span>
        </div>
        <span class="hint trackDuration">${t.durationSeconds ? formatDuration(t.durationSeconds) : ''}</span>
      </div>`).join('')
    : '<p class="hint">Rien en attente.</p>';

  if (!music.current) {
    currentItemId = null;
    setTrackMeta('', 'Rien ne joue pour le moment.', '');
    setMediaSessionIdle();
    if (hls) { hls.destroy(); hls = null; }
    activeDriver!.pause();
    audio.removeAttribute('src'); // harmless no-op while casting - the local element is already srcless then, see startCasting
    (barEl!.querySelector('#trackProgressFill') as HTMLElement).style.width = '0%';
    (barEl!.querySelector('#miniProgressFill') as HTMLElement).style.width = '0%';
  } else if (music.current.itemId !== currentItemId) {
    loadTrack(music.current, music.current.elapsedSeconds, music.current.isPaused);
  } else {
    // Same track still playing - just resync pause state/position via
    // whichever driver is actually active, don't reload the source.
    suppressLocalEvents = true;
    if (Math.abs(activeDriver!.currentTime - music.current.elapsedSeconds) > 2) {
      activeDriver!.currentTime = music.current.elapsedSeconds;
    }
    if (music.current.isPaused && !activeDriver!.paused) activeDriver!.pause();
    if (!music.current.isPaused && activeDriver!.paused) activeDriver!.play();
    setTimeout(() => { suppressLocalEvents = false; }, 250);
  }
}

function renderMembers(members: SocketMember[]): void {
  latestMembers = members;
  updateVoteSkipLabel(); // the connected-member count just changed - see its own comment
  const presenceOverlay = barEl!.querySelector('#presenceOverlay')!;
  presenceOverlay.innerHTML = members.map((m) => `
    <div class="avatar" style="background:${colorForName(m.userName)}" title="${escapeHtml(m.userName)}">
      ${escapeHtml(initialsFor(m.userName))}
    </div>`).join('');
}
